import React, { useState, useEffect } from 'react'
import { Card, FormGroup, Label, Input, Button, Table, Row, Col, FormFeedback } from 'reactstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FaFile, FaVideo, FaLink, FaFileAlt, FaDownload } from 'react-icons/fa'

const RESOURCE_TYPES = [
  { value: 'pdf', label: 'PDF Document', icon: FaFileAlt },
  { value: 'video', label: 'Video', icon: FaVideo },
  { value: 'link', label: 'Web Link', icon: FaLink },
  { value: 'handout', label: 'Patient Handout', icon: FaFile },
  { value: 'other', label: 'Other Resource', icon: FaFile }
]

const TOPICS = [
  { value: 'nutrition', label: 'Nutrition & Diet' },
  { value: 'exercise', label: 'Exercise & Physical Activity' },
  { value: 'medication', label: 'Medication Management' },
  { value: 'screening', label: 'Health Screenings' },
  { value: 'immunization', label: 'Immunizations' },
  { value: 'mental', label: 'Mental Health' },
  { value: 'general', label: 'General Wellness' },
  { value: 'other', label: 'Other' }
]

const EducationalResources = ({ onChange, value = [] }) => {
  const [resources, setResources] = useState(value || [])
  const [editing, setEditing] = useState(false)
  const [editIndex, setEditIndex] = useState(null)
  const [fileUpload, setFileUpload] = useState(null)
  const [uploadError, setUploadError] = useState('')

  useEffect(() => {
    if (onChange && resources) {
      onChange(resources)
    }
  }, [resources, onChange])

  const validationSchema = Yup.object({
    title: Yup.string().required('Resource title is required'),
    type: Yup.string().required('Resource type is required'),
    topic: Yup.string().required('Topic is required'),
    resourceUrl: Yup.string().when('type', {
      is: (type) => type === 'link' || type === 'video',
      then: () => Yup.string().required('URL is required for this resource type').url('Must be a valid URL')
    })
  })

  const formik = useFormik({
    initialValues: {
      title: '',
      type: '',
      topic: '',
      resourceUrl: '',
      description: '',
      fileName: ''
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      // In a real implementation, this would handle file uploads to the server
      // For now, we're just simulating it
      const submittedValues = { ...values }

      if (fileUpload && (values.type === 'pdf' || values.type === 'handout')) {
        submittedValues.fileName = fileUpload.name
        // Here you would upload the file to your server
      }

      if (editing && editIndex !== null) {
        const updatedResources = [...resources]
        updatedResources[editIndex] = submittedValues
        setResources(updatedResources)
        setEditing(false)
        setEditIndex(null)
      } else {
        setResources([...resources, submittedValues])
      }

      setFileUpload(null)
      resetForm()
      setUploadError('')
    }
  })

  const handleEdit = (index) => {
    setEditing(true)
    setEditIndex(index)
    formik.setValues(resources[index])
    setFileUpload(null)
    setUploadError('')
  }

  const handleDelete = (index) => {
    const filteredResources = resources.filter((_, i) => i !== index)
    setResources(filteredResources)
  }

  const handleCancel = () => {
    setEditing(false)
    setEditIndex(null)
    formik.resetForm()
    setFileUpload(null)
    setUploadError('')
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      const fileType = formik.values.type

      // Check file type validation
      if (fileType === 'pdf' && file.type !== 'application/pdf') {
        setUploadError('Please upload a PDF file')
        setFileUpload(null)
        return
      }

      setFileUpload(file)
      formik.setFieldValue('fileName', file.name)
      setUploadError('')
    }
  }

  const getResourceTypeIcon = (type) => {
    const resourceType = RESOURCE_TYPES.find((t) => t.value === type)
    const Icon = resourceType ? resourceType.icon : FaFile
    return <Icon />
  }

  const getTopicLabel = (topicValue) => {
    const topic = TOPICS.find((t) => t.value === topicValue)
    return topic ? topic.label : topicValue
  }

  const getResourceTypeLabel = (typeValue) => {
    const type = RESOURCE_TYPES.find((t) => t.value === typeValue)
    return type ? type.label : typeValue
  }

  return (
    <Card className='p-4'>
      <h4 className='mb-4'>Educational Resources</h4>

      <form onSubmit={formik.handleSubmit}>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for='title'>Resource Title</Label>
              <Input
                type='text'
                id='title'
                name='title'
                placeholder='Enter resource title'
                value={formik.values.title}
                onChange={formik.handleChange}
                invalid={formik.touched.title && formik.errors.title}
              />
              {formik.touched.title && formik.errors.title && <FormFeedback>{formik.errors.title}</FormFeedback>}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for='type'>Resource Type</Label>
              <Input
                type='select'
                id='type'
                name='type'
                value={formik.values.type}
                onChange={formik.handleChange}
                invalid={formik.touched.type && formik.errors.type}
              >
                <option value=''>Select type</option>
                {RESOURCE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Input>
              {formik.touched.type && formik.errors.type && <FormFeedback>{formik.errors.type}</FormFeedback>}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for='topic'>Topic</Label>
              <Input
                type='select'
                id='topic'
                name='topic'
                value={formik.values.topic}
                onChange={formik.handleChange}
                invalid={formik.touched.topic && formik.errors.topic}
              >
                <option value=''>Select topic</option>
                {TOPICS.map((topic) => (
                  <option key={topic.value} value={topic.value}>
                    {topic.label}
                  </option>
                ))}
              </Input>
              {formik.touched.topic && formik.errors.topic && <FormFeedback>{formik.errors.topic}</FormFeedback>}
            </FormGroup>
          </Col>
          <Col md={6}>
            {(formik.values.type === 'link' || formik.values.type === 'video') && (
              <FormGroup>
                <Label for='resourceUrl'>Resource URL</Label>
                <Input
                  type='url'
                  id='resourceUrl'
                  name='resourceUrl'
                  placeholder='Enter resource URL'
                  value={formik.values.resourceUrl}
                  onChange={formik.handleChange}
                  invalid={formik.touched.resourceUrl && formik.errors.resourceUrl}
                />
                {formik.touched.resourceUrl && formik.errors.resourceUrl && (
                  <FormFeedback>{formik.errors.resourceUrl}</FormFeedback>
                )}
              </FormGroup>
            )}
            {(formik.values.type === 'pdf' || formik.values.type === 'handout') && (
              <FormGroup>
                <Label for='resourceFile'>Upload File</Label>
                <Input
                  type='file'
                  id='resourceFile'
                  name='resourceFile'
                  onChange={handleFileChange}
                  accept={formik.values.type === 'pdf' ? '.pdf' : '*'}
                />
                {fileUpload && <div className='mt-2 small text-success'>File selected: {fileUpload.name}</div>}
                {uploadError && <div className='mt-2 small text-danger'>{uploadError}</div>}
              </FormGroup>
            )}
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label for='description'>Description</Label>
              <Input
                type='textarea'
                id='description'
                name='description'
                placeholder='Enter resource description'
                rows='3'
                value={formik.values.description}
                onChange={formik.handleChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <div className='d-flex'>
          <Button
            type='submit'
            color='primary'
            className='mr-2'
            disabled={(formik.values.type === 'pdf' || formik.values.type === 'handout') && !fileUpload && !editing}
          >
            {editing ? 'Update Resource' : 'Add Resource'}
          </Button>
          {editing && (
            <Button type='button' color='secondary' onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      {resources.length > 0 && (
        <div className='mt-4'>
          <h5>Educational Resources</h5>
          <Table striped responsive>
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Topic</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((resource, index) => (
                <tr key={index}>
                  <td>
                    <div className='d-flex align-items-center'>
                      <span className='mr-2'>{getResourceTypeIcon(resource.type)}</span>
                      {resource.title}
                    </div>
                  </td>
                  <td>{getResourceTypeLabel(resource.type)}</td>
                  <td>{getTopicLabel(resource.topic)}</td>
                  <td>{resource.description}</td>
                  <td>
                    <Button color='info' size='sm' className='mr-1' onClick={() => handleEdit(index)}>
                      Edit
                    </Button>
                    <Button color='danger' size='sm' className='mr-1' onClick={() => handleDelete(index)}>
                      Delete
                    </Button>
                    {(resource.type === 'pdf' || resource.type === 'handout') && resource.fileName && (
                      <Button color='success' size='sm'>
                        <FaDownload className='mr-1' /> Download
                      </Button>
                    )}
                    {(resource.type === 'link' || resource.type === 'video') && resource.resourceUrl && (
                      <Button
                        color='success'
                        size='sm'
                        tag='a'
                        href={resource.resourceUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <FaLink className='mr-1' /> Open
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  )
}

export default EducationalResources
